import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import PropTypes from 'prop-types';

// Components
import { Mui } from 'components';

const useStyles = Mui.makeStyles(theme => ({
  header: {
    alignItems: 'center',
    color: theme.palette.text.secondary,
    display: 'flex',
    gridGap: theme.spacing(1),
  },
  count: {
    backgroundColor: theme.palette.text.secondary,
    borderRadius: theme.spacing(1.5),
    color: theme.palette.background.paper,
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: 1,
    padding: theme.spacing(0.5, 0.75),
  },
  item: {
    alignItems: 'center',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    gridGap: theme.spacing(2),
    justifyContent: 'flex-start',
    padding: theme.spacing(1.5, 2),
    textAlign: 'left',
    transition: theme.transitions.create(['border-color', 'background-color']),
    width: '100%',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      borderColor: theme.palette.primary.main,
    },
  },
  icon: {
    alignItems: 'center',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '50%',
    color: theme.palette.primary.contrastText,
    display: 'flex',
    flexShrink: 0,
    height: theme.spacing(4.5),
    justifyContent: 'center',
    width: theme.spacing(4.5),
  },
  text: { minWidth: 0 },
}));

function FeaturedItem({ item }) {
  // Hooks
  const classes = useStyles();

  // Constants
  const linkProps = item.href
    ? { component: 'a', href: item.href }
    : { component: RouterLink, to: item.to };

  return (
    <Mui.ButtonBase
      className={classes.item}
      target="_blank"
      rel="noopener noreferrer"
      {...linkProps}>
      <span className={classes.icon}>
        <Mui.Icon fontSize="small">{item.icon}</Mui.Icon>
      </span>
      <span className={classes.text}>
        <Mui.Typography variant="subtitle2" component="span" display="block">
          {item.title}
        </Mui.Typography>
        {!!item.description && (
          <Mui.Typography variant="caption" color="textSecondary" component="span">
            {item.description}
          </Mui.Typography>
        )}
      </span>
    </Mui.ButtonBase>
  );
}

FeaturedItem.propTypes = {
  item: PropTypes.shape({
    key: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    href: PropTypes.string,
    to: PropTypes.string,
  }).isRequired,
};

function FeaturedList({ title, items }) {
  // Hooks
  const classes = useStyles();

  return (
    <Mui.Box display="flex" flexDirection="column" gridGap={8}>
      <div className={classes.header}>
        <Mui.Icon fontSize="small">my_location</Mui.Icon>
        <Mui.Typography variant="overline" color="inherit">
          <strong>{title}</strong>
        </Mui.Typography>
        <Mui.Typography variant="caption" className={classes.count}>
          {items.length}
        </Mui.Typography>
      </div>
      {items.map(item => (
        <FeaturedItem key={item.key} item={item} />
      ))}
    </Mui.Box>
  );
}

FeaturedList.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
};

export default FeaturedList;
